import React, { useEffect, useState } from 'react';
import '../assets/styles/pages/_seo.scss';
import { axios } from '../axios';

const defaultSection = () => ({
  title_en: '', title_de: '',
  description_en: '', description_de: '',
  keywords_en: '', keywords_de: ''
});

const Seo = () => {
    const [seoData, setSeoData] = useState({
        home: defaultSection(),
        privateTours: defaultSection(),
        groupTours: defaultSection(),
        packages: defaultSection(),
        lesserArmenia: defaultSection(),
        europeTours: defaultSection(),
        aboutArmenia: defaultSection(),
        hotels: defaultSection(),
        apartments: defaultSection(),
        restaurants: defaultSection(),
        embassies: defaultSection(),
        news: defaultSection(),
        guideBook: defaultSection(),
        region: defaultSection(),
    });
    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('none');

    const sectionKeys = [
        'home','privateTours','groupTours','packages','lesserArmenia','europeTours','aboutArmenia',
        'hotels','apartments','restaurants','embassies','news','guideBook','region'
    ];

    const tolerantPick = (obj, enOrDe) => {
        if (!obj) return { title: '', description: '', keywords: '' };
        return {
            title: obj[`title_${enOrDe}`] ?? obj.title ?? '',
            description: obj[`description_${enOrDe}`] ?? obj.description ?? '',
            // allow keywords/key_words and *_en style too
            keywords: obj[`keywords_${enOrDe}`] ?? obj[`key_words_${enOrDe}`] ?? obj.keywords ?? obj.key_words ?? ''
        };
    };

    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            try {
                setLoading(true);
                const reqEn = axios.get('en/seo').catch(() => axios.get('/seo?lang=en'));
                const reqDe = axios.get('de/seo').catch(() => axios.get('/seo?lang=de'));
                const [resEn, resDe] = await Promise.all([reqEn, reqDe]);
                const dataEn = resEn?.data || {};
                const dataDe = resDe?.data || {};

                if (!mounted) return;
                setSeoData(prev => {
                    const updated = { ...prev };
                    sectionKeys.forEach(key => {
                        const en = tolerantPick(dataEn[key], 'en');
                        const de = tolerantPick(dataDe[key], 'de');
                        updated[key] = {
                            title_en: en.title,
                            title_de: de.title,
                            description_en: en.description,
                            description_de: de.description,
                            keywords_en: en.keywords,
                            keywords_de: de.keywords,
                        };
                    });
                    return updated;
                });
                setSubmitStatus('none');
            } catch (e) {
                setSubmitStatus('error-load');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        return () => { mounted = false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (page, e) => {
        const { name, value } = e.target;
        setSeoData(prev => ({
            ...prev,
            [page]: {
                ...prev[page],
                [name]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitStatus('load');
            // Send normalized payload
            const res = await axios.post('/seo', seoData);
            if (res?.data === 'updated' || res?.data?.message === 'updated') {
                setSubmitStatus('updated');
            } else {
                setSubmitStatus('error');
            }
        } catch (e) {
            setSubmitStatus('error');
        }
    };

    const renderForm = (page, pageTitle) => (
        <div className="seo-form">
            <h2>{pageTitle} SEO Settings</h2>
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor={`${page}-title_en`}>Meta Title (EN):</label>
                    <input
                        type="text"
                        id={`${page}-title_en`}
                        name="title_en"
                        value={seoData[page].title_en}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${page}-title_de`}>Meta Title (DE):</label>
                    <input
                        type="text"
                        id={`${page}-title_de`}
                        name="title_de"
                        value={seoData[page].title_de}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor={`${page}-description_en`}>Meta Description (EN):</label>
                    <textarea
                        id={`${page}-description_en`}
                        name="description_en"
                        value={seoData[page].description_en}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${page}-description_de`}>Meta Description (DE):</label>
                    <textarea
                        id={`${page}-description_de`}
                        name="description_de"
                        value={seoData[page].description_de}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label htmlFor={`${page}-keywords_en`}>Meta Keywords (EN):</label>
                    <input
                        type="text"
                        id={`${page}-keywords_en`}
                        name="keywords_en"
                        value={seoData[page].keywords_en}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
                <div className="form-group">
                    <label htmlFor={`${page}-keywords_de`}>Meta Keywords (DE):</label>
                    <input
                        type="text"
                        id={`${page}-keywords_de`}
                        name="keywords_de"
                        value={seoData[page].keywords_de}
                        onChange={(e) => handleChange(page, e)}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100vh',
            width:'100%',
            overflow:'scroll',
            padding:'50px 0'
        }}>
            <div className="seo-container">
                <h1>SEO Settings</h1>
                {loading && <p style={{textAlign:'center'}}>Loading SEO...</p>}
                {submitStatus === 'updated' && <p style={{color:'green'}}>Saved successfully.</p>}
                {submitStatus === 'error' && <p style={{color:'red'}}>Save failed. Please try again.</p>}
                <form onSubmit={handleSubmit} className="seo-grid">
                    {renderForm('home', 'Home')}
                    {renderForm('privateTours', 'Private Tours')}
                    {renderForm('groupTours', 'Group Tours')}
                    {renderForm('packages', 'Packages')}
                    {renderForm('lesserArmenia', 'Lesser Armenia')}
                    {renderForm('europeTours', 'Europe Tours')}
                    {renderForm('aboutArmenia', 'About Armenia')}
                    {renderForm('hotels', 'Hotels')}
                    {renderForm('apartments', 'Apartments')}
                    {renderForm('restaurants', 'Restaurants')}
                    {renderForm('embassies', 'Embassies')}
                    {renderForm('news', 'News')}
                    {renderForm('guideBook', 'GuideBook')}
                    {renderForm('region', 'Region')}
                    <button type="submit" className="save-button" disabled={submitStatus==='load'}>
                        {submitStatus==='load' ? 'Saving...' : 'Save'}
                    </button>
                </form>
            </div>
        </div>

    );
};

export default Seo;